// Zero-knowledge crypto for one-time secret sharing.
//
// Encryption and decryption happen entirely in the browser (Web Crypto,
// AES-256-GCM). The random key is returned to the caller so it can be placed in
// the share link's URL fragment (`#...`) and never sent to the server. The
// server only ever stores `ciphertext` + `iv`.
//
// Encodings:
//   - ciphertext, iv: standard base64 (travel in the JSON request body)
//   - key: base64url (travels in the URL fragment, must be URL-safe)

function bufToBase64(buffer) {
	const bytes = new Uint8Array(buffer);
	let binary = "";
	for (let i = 0; i < bytes.length; i++) {
		binary += String.fromCharCode(bytes[i]);
	}
	return btoa(binary);
}

function base64ToBuf(base64) {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

function bufToBase64Url(buffer) {
	return bufToBase64(buffer)
		.replace(/\+/g, "-")
		.replace(/\//g, "_")
		.replace(/=+$/, "");
}

function base64UrlToBuf(base64url) {
	let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
	while (base64.length % 4) {
		base64 += "=";
	}
	return base64ToBuf(base64);
}

// Encrypt plaintext. Returns { ciphertext, iv } (base64) and key (base64url).
async function encryptSecret(plaintext) {
	const key = await crypto.subtle.generateKey(
		{ name: "AES-GCM", length: 256 },
		true,
		["encrypt", "decrypt"]
	);
	const iv = crypto.getRandomValues(new Uint8Array(12));
	const encoded = new TextEncoder().encode(plaintext);
	const ciphertext = await crypto.subtle.encrypt(
		{ name: "AES-GCM", iv },
		key,
		encoded
	);
	const rawKey = await crypto.subtle.exportKey("raw", key);
	return {
		ciphertext: bufToBase64(ciphertext),
		iv: bufToBase64(iv),
		key: bufToBase64Url(rawKey),
	};
}

// Decrypt using ciphertext + iv (base64) and key (base64url). Throws on failure
// (wrong/corrupted key or tampered ciphertext).
async function decryptSecret({ ciphertext, iv, key }) {
	const cryptoKey = await crypto.subtle.importKey(
		"raw",
		base64UrlToBuf(key),
		{ name: "AES-GCM", length: 256 },
		false,
		["decrypt"]
	);
	const plaintext = await crypto.subtle.decrypt(
		{ name: "AES-GCM", iv: base64ToBuf(iv) },
		cryptoKey,
		base64ToBuf(ciphertext)
	);
	return new TextDecoder().decode(plaintext);
}

export default { encryptSecret, decryptSecret };
